
import { useState, useEffect, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  placeholder?: string;
  className?: string;
  loadingStyles?: string;
  priority?: boolean; // New prop for above-the-fold images
  blurUpPreview?: boolean; // New prop for blur-up effect
}

export function LazyImage({ 
  src, 
  alt, 
  placeholder = '/placeholder.svg',
  className = '',
  loadingStyles = 'rounded-md',
  priority = false,
  blurUpPreview = false,
  ...props 
}: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Priority images start as "in view"
  const [imageSrc, setImageSrc] = useState<string>(blurUpPreview ? placeholder : (priority ? src : placeholder));
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Cleanup function for the observer
  const cleanupObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  };

  // Setup the intersection observer
  useEffect(() => {
    // Skip observer for priority images - load them immediately
    if (priority) {
      setImageSrc(src);
      return;
    }

    // Create the observer for non-priority images
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Load the actual image
          setImageSrc(src);
          // Disconnect the observer once the image is in view
          cleanupObserver();
        }
      },
      {
        rootMargin: '200px', // Load images 200px before they enter the viewport
        threshold: 0.01
      }
    );

    // Start observing if we have a reference to the image
    if (imgRef.current && observerRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    // Cleanup on unmount or when src changes
    return cleanupObserver;
  }, [src, priority]);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  // Handle image load error
  const handleImageError = () => {
    if (imageSrc !== placeholder) {
      console.warn(`Failed to load image: ${src}, falling back to placeholder`);
      setImageSrc(placeholder);
    }
    setIsLoaded(true);
  };

  return (
    <div className={`relative ${className}`} ref={imgRef}>
      {!isLoaded && (
        <Skeleton className={`absolute inset-0 w-full h-full ${loadingStyles}`} />
      )}
      <img
        src={imageSrc}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        className={`w-full h-full transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        } ${blurUpPreview && !isLoaded ? 'blur-lg scale-105' : 'blur-0 scale-100'}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        {...props}
      />
    </div>
  );
}
